import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, LockKey } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";

export default function Login() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // MFA challenge state
  const [mfaToken, setMfaToken] = useState("");
  const [mfaEmail, setMfaEmail] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  // Forced-enrolment state
  const [setupToken, setSetupToken] = useState("");
  const [setupData, setSetupData] = useState(null); // { secret, otpauth_uri, email }
  const [setupCode, setSetupCode] = useState("");
  const [enrollReason, setEnrollReason] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) {
        setMfaToken(data.mfa_token);
        setMfaEmail(data.email || email);
        toast.info("Enter your 6-digit code");
      } else if (data.mfa_setup_required) {
        setSetupToken(data.setup_token);
        setMfaEmail(data.email || email);
        setEnrollReason(data.reason || "");
        toast.info("MFA enrolment required by policy");
        // auto-generate the secret in one shot
        try {
          const r = await api.post("/auth/mfa/setup-forced", { setup_token: data.setup_token });
          setSetupData(r.data);
        } catch (e2) {
          setErr(formatApiError(e2.response?.data?.detail) || "Enrolment failed");
        }
      } else {
        await refresh();
        toast.success("Welcome back");
        // Iter-30: buyers land on /buyer, vendors on /vendor (both read-only).
        const me = await api.get("/auth/me").then((r) => r.data?.user).catch(() => null);
        if (me?.role === "buyer") {
          nav("/buyer", { replace: true });
        } else if (me?.role === "vendor") {
          nav("/vendor", { replace: true });
        } else {
          nav("/", { replace: true });
        }
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(mfaCode)) return setErr("Enter a 6-digit code");
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/mfa/login", { mfa_token: mfaToken, code: mfaCode });
      await refresh();
      toast.success("Welcome back");
      nav("/", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const submitEnroll = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(setupCode)) return setErr("Enter a 6-digit code");
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/mfa/verify-forced", { setup_token: setupToken, code: setupCode });
      await refresh();
      toast.success("MFA enabled — welcome");
      nav("/", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen h-screen grid overflow-hidden" style={{ gridTemplateColumns: "1fr 1fr" }}>
      <div className="hidden md:flex flex-col justify-between p-10 border-r border-black bg-white overflow-hidden">
        <div>
          <div className="font-chivo font-black text-[64px] leading-[0.9] tracking-tighter uppercase">
            Lead<br />Flow<span className="text-red-600">.</span>
          </div>
          <div className="label-mono mt-4">web lead operations platform · v1</div>
        </div>
        <div className="grid grid-cols-4 gap-1 max-w-xs">
          {[..."0011010111001010".split("")].map((c, i) => (
            <div key={`bit-${i}-${c}`} className="aspect-square border border-black"
              style={{ background: c === "1" ? "#000" : "#fff" }} />
          ))}
        </div>
        <div className="text-sm max-w-md leading-relaxed">
          <div className="uppercase text-[10px] tracking-widest text-zinc-500 mb-2">what this is</div>
          A single operational surface for ingesting web leads, tracking
          quality issues, running the return + credit workflow, and keeping
          every action auditable across internal, client and affiliate teams.
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm brut-card p-8 brut-shadow-lg">
          {!mfaToken && !setupToken && (
            <>
              <div className="label-mono">Sign in</div>
              <div className="font-chivo font-black text-3xl uppercase tracking-tighter leading-none mt-1">
                Operator access
              </div>
              <form className="mt-8 space-y-5" onSubmit={submit}>
                <div>
                  <label className="label-mono">Email</label>
                  <input data-testid="login-email" type="email" required autoComplete="email"
                    className="w-full border border-black px-3 py-2 mt-1 bg-white focus:outline-none focus:ring-2 focus:ring-black"
                    value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div>
                  <label className="label-mono">Password</label>
                  <input data-testid="login-password" type="password" required autoComplete="current-password"
                    className="w-full border border-black px-3 py-2 mt-1 bg-white focus:outline-none focus:ring-2 focus:ring-black"
                    value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                {err && (
                  <div data-testid="login-error" className="border border-red-600 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {err}
                  </div>
                )}
                <button data-testid="login-submit" type="submit" disabled={loading}
                  className="w-full bg-black text-white py-3 border border-black uppercase tracking-widest text-xs font-bold hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100 disabled:opacity-60">
                  {loading ? "Signing in…" : (
                    <span className="inline-flex items-center gap-2">Sign in <ArrowRight size={14} weight="bold" /></span>
                  )}
                </button>
                <div className="flex justify-between text-[11px] uppercase tracking-widest">
                  <Link to="/forgot-password" className="hover:underline" data-testid="forgot-link">Forgot password</Link>
                  <span className="text-zinc-500">JWT · secure</span>
                </div>
              </form>
            </>
          )}
          {mfaToken && !setupToken && (
            <>
              <div className="label-mono">Two-factor</div>
              <div className="font-chivo font-black text-3xl uppercase tracking-tighter leading-none mt-1">
                Enter code
              </div>
              <div className="mt-3 text-xs text-zinc-600 flex items-center gap-1">
                <ShieldCheck size={12} weight="bold" /> Signing in as <span className="font-mono">{mfaEmail}</span>
              </div>
              <form className="mt-6 space-y-5" onSubmit={submitMfa}>
                <div>
                  <label className="label-mono">6-digit code</label>
                  <input data-testid="mfa-login-code" required inputMode="numeric"
                    pattern="[0-9]{6}" maxLength={6} autoFocus
                    className="w-full border border-black px-3 py-2 mt-1 font-mono text-2xl tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-black"
                    value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))} />
                </div>
                {err && (
                  <div data-testid="mfa-login-error" className="border border-red-600 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {err}
                  </div>
                )}
                <button data-testid="mfa-login-submit" type="submit" disabled={loading}
                  className="w-full bg-black text-white py-3 border border-black uppercase tracking-widest text-xs font-bold">
                  {loading ? "Verifying…" : "Verify & continue"}
                </button>
                <button type="button" onClick={() => { setMfaToken(""); setMfaCode(""); setErr(""); }}
                  className="w-full border border-black py-2 uppercase tracking-widest text-[11px]">
                  ← Back
                </button>
              </form>
            </>
          )}
          {setupToken && (
            <>
              <div className="label-mono">Required enrolment</div>
              <div className="font-chivo font-black text-2xl uppercase tracking-tighter leading-none mt-1">
                Set up MFA to continue
              </div>
              <div className="mt-3 text-xs text-zinc-600 flex items-center gap-1">
                <LockKey size={12} weight="bold" /> {mfaEmail}
                {enrollReason === "mfa_policy_grace_expired" && (
                  <span className="ml-2 bg-yellow-200 border border-yellow-500 px-2 py-0.5 text-[10px] uppercase tracking-widest">
                    Grace expired
                  </span>
                )}
              </div>
              {setupData ? (
                <form className="mt-4 space-y-4" onSubmit={submitEnroll}>
                  <div className="border border-black p-3 flex items-center gap-3">
                    <div className="border border-black p-2 bg-white">
                      <QRCodeSVG value={setupData.otpauth_uri} size={110} />
                    </div>
                    <div className="text-[10px]">
                      <div className="label-mono mb-1">Scan with Authenticator</div>
                      <details>
                        <summary className="cursor-pointer">Show secret</summary>
                        <div className="mt-1 break-all bg-zinc-100 p-1 border border-zinc-300 font-mono">
                          {setupData.secret}
                        </div>
                      </details>
                    </div>
                  </div>
                  <div>
                    <label className="label-mono">6-digit code</label>
                    <input data-testid="mfa-enroll-code" required inputMode="numeric"
                      pattern="[0-9]{6}" maxLength={6} autoFocus
                      className="w-full border border-black px-3 py-2 mt-1 font-mono text-2xl tracking-widest text-center"
                      value={setupCode} onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, ""))} />
                  </div>
                  {err && (
                    <div data-testid="mfa-enroll-error" className="border border-red-600 bg-red-50 px-3 py-2 text-xs text-red-700">
                      {err}
                    </div>
                  )}
                  <button data-testid="mfa-enroll-submit" type="submit" disabled={loading}
                    className="w-full bg-black text-white py-3 border border-black uppercase tracking-widest text-xs font-bold">
                    {loading ? "Enrolling…" : "Confirm & sign in"}
                  </button>
                  <button type="button" onClick={() => { setSetupToken(""); setSetupData(null); setSetupCode(""); setErr(""); }}
                    className="w-full border border-black py-2 uppercase tracking-widest text-[11px]">
                    ← Cancel
                  </button>
                </form>
              ) : (
                <div className="mt-4 text-xs text-zinc-600">Preparing MFA setup…</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
